import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {NotificationManager} from 'react-notifications';
import {Link} from 'react-router-dom';
import {API_ENDPOINT} from '../config';
import {useAuth} from '../auth/AuthContext';

/**
 * Cere (re)generarea APK-ului pentru companiile userului logat.
 * POST .../generate-apk creeaza o cerere "queued" in DB (ApkGenerationRequest) -
 * generarea efectiva e ridicata de un script separat de pe host (nu inca scris,
 * aug 2026 - vezi docs/ARCHITECTURE.md). Pana atunci cererile raman "queued".
 */
const STATUS_LABELS = {
    queued: {text: 'În coadă', badge: 'secondary'},
    running: {text: 'Se generează', badge: 'info'},
    success: {text: 'Reușit', badge: 'success'},
    failed: {text: 'Eșuat', badge: 'danger'},
};

const formatTimeRemaining = (expiresAtIso) => {
    const diffMs = new Date(expiresAtIso).getTime() - Date.now();
    if (diffMs <= 0) {
        return 'Expirat';
    }
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (days >= 1) {
        return `Disponibil încă ${days} ${days === 1 ? 'zi' : 'zile'}`;
    }
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    if (hours >= 1) {
        return `Disponibil încă ${hours} ${hours === 1 ? 'oră' : 'ore'}`;
    }
    const minutes = Math.max(1, Math.floor(diffMs / (60 * 1000)));
    return `Disponibil încă ${minutes} min`;
};

const ApkGenerator = () => {
    const {auth, logout} = useAuth();
    const [companies, setCompanies] = useState([]);
    const [activeCode, setActiveCode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [requesting, setRequesting] = useState(false);
    const [, setTick] = useState(0);

    // re-randeaza o data pe minut, ca textul "Disponibil inca X" sa ramana corect
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 60000);
        return () => clearInterval(interval);
    }, []);

    const handleDownload = async (requestId, suggestedName) => {
        try {
            const {data} = await axios.get(
                `${API_ENDPOINT}/v1/me/companies/${activeCode}/apk-generations/${requestId}/download`,
                {headers: authHeader, responseType: 'blob'}
            );
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = suggestedName || 'app.apk';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
            } else {
                NotificationManager.error('Nu am putut descărca fișierul (poate a expirat).');
            }
        }
    };

    const authHeader = {Authorization: `Bearer ${auth.token}`};

    const loadMe = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await axios.get(`${API_ENDPOINT}/v1/me`, {headers: authHeader});
            const list = data.companies || [];
            setCompanies(list);
            setActiveCode((prev) => prev && list.some(c => c.code === prev) ? prev : (list[0]?.code ?? null));
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
            } else {
                NotificationManager.error('Nu am putut încărca datele companiei.');
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token]);

    const loadHistory = useCallback(async (code) => {
        if (!code) {
            setHistory([]);
            return;
        }
        try {
            const {data} = await axios.get(`${API_ENDPOINT}/v1/me/companies/${code}/apk-generations`, {headers: authHeader});
            setHistory(data || []);
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token]);

    useEffect(() => {
        loadMe();
    }, [loadMe]);

    useEffect(() => {
        loadHistory(activeCode);
    }, [activeCode, loadHistory]);

    // cat exista o cerere in curs (queued/running), verificam periodic statusul
    useEffect(() => {
        const hasPending = history.some(h => h.status === 'queued' || h.status === 'running');
        if (!hasPending || !activeCode) {
            return;
        }
        const interval = setInterval(() => loadHistory(activeCode), 15000);
        return () => clearInterval(interval);
    }, [history, activeCode, loadHistory]);

    const handleGenerate = async () => {
        if (!activeCode) {
            return;
        }
        setRequesting(true);
        try {
            await axios.post(
                `${API_ENDPOINT}/v1/me/companies/${activeCode}/generate-apk`,
                {},
                {headers: authHeader}
            );
            NotificationManager.success('Cererea de generare a fost înregistrată.');
            await loadHistory(activeCode);
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
            } else {
                NotificationManager.error(err.response?.data?.error || 'Nu am putut trimite cererea.');
            }
        } finally {
            setRequesting(false);
        }
    };

    const activeCompany = companies.find(c => c.code === activeCode) || null;
    const hasPendingRequest = history.some(h => h.status === 'queued' || h.status === 'running');
    // Fara logo, poll_and_generate.sh oprea generarea abia dupa ce cererea intra
    // in coada - userul afla din istoric, nu din formular. Il prindem aici.
    const hasLogo = Boolean(activeCompany?.logo);

    if (loading) {
        return <p className="text-muted">Se încarcă…</p>;
    }

    if (companies.length === 0) {
        return <p className="text-muted">Nu aveți încă o companie înregistrată.</p>;
    }

    return (
        <div>
            <ul className="nav nav-tabs mb-3">
                {companies.map(c => (
                    <li className="nav-item" key={c.code}>
                        <button
                            type="button"
                            className={`nav-link ${c.code === activeCode ? 'active' : ''}`}
                            onClick={() => setActiveCode(c.code)}
                        >
                            {c.name}
                        </button>
                    </li>
                ))}
            </ul>

            <div className="card card-outline card-primary">
                <div className="card-header">
                    <h3 className="card-title mb-0">
                        <i className="fas fa-mobile-alt mr-2"></i>
                        Generare APK — {activeCompany?.name}
                    </h3>
                </div>
                <div className="card-body">
                    <p className="text-muted">
                        Trimite o cerere de (re)generare a aplicației mobile pentru compania selectată.
                        Generarea nu este instantă — cererea intră într-o coadă și este procesată separat.
                    </p>
                    {!hasLogo && (
                        <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle mr-1"></i>
                            Compania nu are logo încărcat, iar generarea nu poate porni fără el —
                            din logo se creează iconița aplicației.
                            <div className="mt-2">
                                <Link to="/logo" className="btn btn-warning btn-sm">
                                    <i className="far fa-image mr-1"></i> Încarcă logo
                                </Link>
                            </div>
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={requesting || hasPendingRequest || !hasLogo}
                        onClick={handleGenerate}
                        title={
                            !hasLogo
                                ? 'Încarcă întâi logo-ul companiei'
                                : (hasPendingRequest ? 'Există deja o cerere în curs pentru această companie' : undefined)
                        }
                    >
                        {requesting
                            ? <><i className="fas fa-spinner fa-spin mr-1"></i> Se trimite…</>
                            : <><i className="fas fa-cogs mr-1"></i> Generează APK</>}
                    </button>
                </div>
                <div className="card-body border-top p-0">
                    {history.length === 0 ? (
                        <p className="text-muted p-3 mb-0">Nicio cerere de generare încă.</p>
                    ) : (
                        <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead>
                            <tr>
                                <th>Solicitat</th>
                                <th></th>
                                <th>Status</th>
                                <th>Finalizat</th>
                                <th className="d-none d-md-table-cell">Descarcă</th>
                            </tr>
                            </thead>
                            <tbody>
                            {history.map(h => {
                                const label = STATUS_LABELS[h.status] || {text: h.status, badge: 'secondary'};
                                return (
                                    <tr key={h.id}>
                                        <td style={{ whiteSpace: "nowrap" }}>{new Date(h.requestedAt).toLocaleString('ro-RO')}</td>
                                        <td>
                                            {h.status === 'running' && (
                                                <img style={{ width: 20, height: 20 }} src="/dist/img/loading220.gif" alt="Se generează" className="img-size-50 mr-3 img-circle"/>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`badge badge-${label.badge}`}>{label.text}</span>
                                            {h.status === 'failed' && h.errorMessage && (
                                                <small className="text-danger d-block" style={{wordBreak: "break-word"}}>{h.errorMessage}</small>
                                            )}
                                        </td>
                                        <td>{h.finishedAt ? new Date(h.finishedAt).toLocaleString('ro-RO') : '—'}</td>
                                        <td className="d-block d-md-table-cell">
                                            {h.fileAvailable ? (
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleDownload(h.id, `app_${activeCode}.apk`)}
                                                    >
                                                        <i className="fas fa-download mr-1"></i> Descarcă
                                                    </button>
                                                    <small className="text-muted d-block mt-1">{formatTimeRemaining(h.fileExpiresAt)}</small>
                                                </div>
                                            ) : (h.status === 'success' ? <small className="text-muted">Expirat</small> : '—')}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApkGenerator;
